import { cn } from "@/lib/utils";

interface ScoreBarProps {
  score: number;
  maxScore?: number;
  /** Display mode: "percent" (default) shows "{value}%"; "raw" shows "{value}/{maxScore}"; "number" shows "{value}" */
  format?: "percent" | "raw" | "number";
  /**
   * Label thresholds expressed on a 0-100 scale (inclusive lower bounds).
   * Defaults to 70/40 with Strong/Moderate/Weak.
   */
  labels?: {
    high: { threshold: number; label: string };
    mid: { threshold: number; label: string };
    low: { label: string };
  };
}

const DEFAULT_LABELS: NonNullable<ScoreBarProps["labels"]> = {
  high: { threshold: 70, label: "Strong" },
  mid: { threshold: 40, label: "Moderate" },
  low: { label: "Weak" },
};

export function ScoreBar({
  score,
  maxScore = 100,
  format = "percent",
  labels = DEFAULT_LABELS,
}: ScoreBarProps) {
  const normalized = Math.round((score / maxScore) * 100);
  const qualifier =
    normalized >= labels.high.threshold
      ? labels.high.label
      : normalized >= labels.mid.threshold
        ? labels.mid.label
        : labels.low.label;

  const display =
    format === "raw"
      ? `${score}/${maxScore}`
      : format === "number"
        ? `${score}`
        : `${normalized}%`;

  return (
    <span
      className={cn(
        "whitespace-nowrap font-mono text-xs tabular-nums",
        normalized >= labels.high.threshold
          ? "text-green-700 dark:text-green-400"
          : normalized >= labels.mid.threshold
            ? "text-foreground"
            : "text-muted-foreground/50"
      )}
    >
      {display} <span className="text-[10px] font-normal">{qualifier}</span>
    </span>
  );
}
